import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Users, Activity, LineChart, ArrowLeft, Briefcase, CreditCard, LayoutDashboard, Inbox, MailPlus, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const NAV = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/contacts', label: 'Contacts', icon: Inbox },
  { to: '/admin/email', label: 'Email', icon: MailPlus },
  { to: '/admin/signals', label: 'Signals', icon: LineChart },
  { to: '/admin/executions', label: 'Executions', icon: Activity },
  { to: '/admin/trades', label: 'Trades', icon: Briefcase },
  { to: '/admin/billing', label: 'Billing', icon: CreditCard },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
];

export default function AdminLayout({ children, title, description }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Preserve the admin path the user was trying to reach so they bounce back after Google.
      const intended = location.pathname + (location.search || '');
      sessionStorage.setItem('post_login_redirect', intended);
      navigate(`/login?next=${encodeURIComponent(intended)}`, { replace: true });
      return;
    }
    if (!user.isAdmin) {
      setDenied(true);
    }
  }, [user, loading, navigate, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <p className="text-zinc-500 text-sm font-mono">Loading…</p>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Shield className="mx-auto gold-text" size={32} />
          <h1 className="mt-4 font-display text-3xl font-bold text-white">Admin only</h1>
          <p className="mt-3 text-sm text-zinc-400">Your account does not have admin access.</p>
          <Link to="/dashboard" className="mt-6 inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-zinc-200 hover:border-[#d8b34a]/50 rounded-sm text-sm">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col" data-testid="admin-layout">
      <div className="border-b border-white/5 bg-[#0c0c0e] sticky top-0 z-30 backdrop-blur">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1.5 font-mono">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <span className="h-4 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <Shield size={14} className="gold-text" />
              <span className="font-mono text-xs tracking-[0.3em] gold-text">TALAS ADMIN</span>
            </div>
          </div>
          <nav className="flex items-center gap-1.5" data-testid="admin-nav">
            {NAV.map(item => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-sm border transition-all ${
                    active
                      ? 'border-[#d8b34a]/50 text-white bg-[#d8b34a]/[0.06]'
                      : 'border-white/10 text-zinc-400 hover:text-white hover:border-white/20'
                  }`}
                >
                  <Icon size={14} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="flex-1">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 py-10">
          <div className="pb-6 border-b border-white/5">
            <p className="text-xs tracking-[0.4em] font-mono text-zinc-500">{title?.toUpperCase()}</p>
            {description && <p className="mt-3 text-sm text-zinc-400">{description}</p>}
          </div>
          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
